import { useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { GrantSpaceAccessModal } from "../components/GrantSpaceAccessModal";
import { grantForMutation } from "../lib/grantAssembly";
import type { SupplierGap } from "../lib/spaceMemberSupplierGap";
import { userDisplayName } from "../lib/userDisplayName";
import { useQuery } from "./useQuery";

type Pending = {
  spaceName: string;
  gaps: SupplierGap[];
  proceed: () => void;
};

// Pre-flights adding a member to a space: if they'd lack any connector/skill the
// space uses, opens a modal to grant those the admin can share, then runs the
// original member-add. `guard` runs the add directly when there's no gap.
export function useGrantSpaceAccess() {
  const z = useZero();
  const [allUsers] = useQuery(queries.allUsers());
  const [allGroups] = useQuery(queries.selectableUserGroups());
  const [pending, setPending] = useState<Pending | null>(null);

  const userNames = useMemo(
    () => new Map(allUsers.map((u) => [u.id, userDisplayName(u)] as const)),
    [allUsers]
  );
  const groupNames = useMemo(
    () => new Map(allGroups.map((g) => [g.id, g.name] as const)),
    [allGroups]
  );

  // Returns true when it opened the modal (commit deferred until confirm), so the
  // caller can keep the member staged in the list behind it.
  const guard = useCallback(
    (args: { spaceName: string; gaps: SupplierGap[]; proceed: () => void }) => {
      if (args.gaps.length === 0) {
        args.proceed();
        return false;
      }
      setPending(args);
      return true;
    },
    []
  );

  const onConfirm = useCallback(() => {
    if (!pending) return;
    const grantable = pending.gaps.filter((g) => g.canShare);
    if (grantable.length === pending.gaps.length) {
      // Every gap is shareable: proceed() runs patchScopeGrants, which grants
      // the suppliers and adds the member in one atomic transaction.
      pending.proceed();
    } else {
      // Some suppliers the admin can't share: grant the ones they can and leave
      // the member out (adding them would fail the cross-resource guard on the
      // rest). Each is a pure additive grant on that supplier — the member-add
      // via proceed() is intentionally skipped.
      for (const gap of grantable) {
        z.mutate(
          sharedMutators.patchScopeGrants({
            scopeId: gap.supplierId,
            scopeKind: gap.scopeKind,
            add: gap.additions.map(grantForMutation),
            remove: [],
          })
        );
      }
    }
    setPending(null);
  }, [z, pending]);

  const modal = pending ? (
    <GrantSpaceAccessModal
      open
      onClose={() => setPending(null)}
      spaceName={pending.spaceName}
      gaps={pending.gaps}
      userNames={userNames}
      groupNames={groupNames}
      onConfirm={onConfirm}
    />
  ) : null;

  return { guard, modal };
}
